import { useState, useCallback } from 'react';
import type {
  BlogPost,
  BlogPostListItem,
  BlogCategory,
  BlogTag,
  BlogMedia,
  BlogFont,
  BlogAnalyticsDashboard,
  BlogTab,
} from '../types';
import * as blogService from '../services/blog.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseBlogProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

export function useBlog({ accessToken, refreshToken, attemptRefresh }: UseBlogProps) {
  // Data states
  const [posts, setPosts] = useState<BlogPostListItem[]>([]);
  const [currentPost, setCurrentPost] = useState<BlogPost | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [media, setMedia] = useState<BlogMedia[]>([]);
  const [fonts, setFonts] = useState<BlogFont[]>([]);
  const [analytics, setAnalytics] = useState<BlogAnalyticsDashboard | null>(null);

  // UI states
  const [loading, setLoading] = useState(false);
  const [blogTab, setBlogTabState] = useState<BlogTab>('posts');
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [totalPosts, setTotalPosts] = useState(0);
  const [postsPage, setPostsPage] = useState(0);

  // ============================================================
  // Auth guard helper
  // ============================================================

  function notAuthenticated(): { success: false; error: string } {
    return { success: false, error: 'Não autenticado' };
  }

  // ============================================================
  // Posts
  // ============================================================

  const loadPosts = useCallback(
    async (status?: string, search?: string, page = 0): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        const result = await withAuthRetry(
          () => blogService.fetchPosts({ page, size: 20, status, search }),
          refreshToken,
          attemptRefresh
        );
        setPosts(result.posts ?? []);
        setTotalPosts(result.total ?? 0);
        setPostsPage(page);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar posts' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const loadPost = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        const post = await withAuthRetry(
          () => blogService.fetchPost(id),
          refreshToken,
          attemptRefresh
        );
        setCurrentPost(post);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar post' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const handleCreatePost = useCallback(
    async (data: Partial<BlogPost>): Promise<{ success: boolean; error?: string; post?: BlogPost }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        const post = await withAuthRetry(
          () => blogService.createPost(data),
          refreshToken,
          attemptRefresh
        );
        setCurrentPost(post);
        setEditingPostId(post.id);
        return { success: true, post };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar post' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const handleUpdatePost = useCallback(
    async (id: string, data: Partial<BlogPost>): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        const updated = await withAuthRetry(
          () => blogService.updatePost(id, data),
          refreshToken,
          attemptRefresh
        );
        setCurrentPost(updated);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar post' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  const handlePublishPost = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.publishPost(id),
          refreshToken,
          attemptRefresh
        );
        await loadPosts();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao publicar post' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadPosts]
  );

  const handleDeletePost = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.deletePost(id),
          refreshToken,
          attemptRefresh
        );
        await loadPosts();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir post' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadPosts]
  );

  const handleAutoSave = useCallback(
    async (id: string, content: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      try {
        await withAuthRetry(
          () => blogService.autoSavePost(id, content),
          refreshToken,
          attemptRefresh
        );
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao salvar automaticamente' };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // ============================================================
  // Categories
  // ============================================================

  const loadCategories = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return notAuthenticated();
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => blogService.fetchCategories(),
        refreshToken,
        attemptRefresh
      );
      setCategories(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar categorias' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ============================================================
  // Tags
  // ============================================================

  const loadTags = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return notAuthenticated();
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => blogService.fetchTags(),
        refreshToken,
        attemptRefresh
      );
      setTags(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar tags' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ============================================================
  // Media
  // ============================================================

  const loadMedia = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return notAuthenticated();
    setLoading(true);
    try {
      const result = await withAuthRetry(
        () => blogService.fetchMedia(),
        refreshToken,
        attemptRefresh
      );
      setMedia(result.media ?? result.items ?? []);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar mídia' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ============================================================
  // Fonts
  // ============================================================

  const loadFonts = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return notAuthenticated();
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => blogService.fetchFonts(),
        refreshToken,
        attemptRefresh
      );
      setFonts(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar fontes' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ============================================================
  // Analytics
  // ============================================================

  const loadAnalytics = useCallback(
    async (from?: string, to?: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        const data = await withAuthRetry(
          () => blogService.fetchAnalyticsDashboard(from, to),
          refreshToken,
          attemptRefresh
        );
        setAnalytics(data);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar analytics' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // ============================================================
  // Tab management
  // ============================================================

  const setBlogTab = useCallback(
    (tab: BlogTab) => {
      setBlogTabState(tab);
      if (tab === 'posts') {
        loadPosts();
      } else if (tab === 'analytics') {
        loadAnalytics();
      } else if (tab === 'settings') {
        loadCategories();
        loadTags();
        loadFonts();
      }
    },
    [loadPosts, loadAnalytics, loadCategories, loadTags, loadFonts]
  );

  // ============================================================
  // Category mutations
  // ============================================================

  const handleCreateCategory = useCallback(
    async (data: { name: string; description?: string; color?: string }): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.createCategory(data),
          refreshToken,
          attemptRefresh
        );
        await loadCategories();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar categoria' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadCategories]
  );

  const handleUpdateCategory = useCallback(
    async (
      id: string,
      data: { name?: string; description?: string; color?: string; sortOrder?: number }
    ): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.updateCategory(id, data),
          refreshToken,
          attemptRefresh
        );
        await loadCategories();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar categoria' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadCategories]
  );

  const handleDeleteCategory = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.deleteCategory(id),
          refreshToken,
          attemptRefresh
        );
        await loadCategories();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir categoria' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadCategories]
  );

  // ============================================================
  // Tag mutations
  // ============================================================

  const handleCreateTag = useCallback(
    async (name: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.createTag({ name }),
          refreshToken,
          attemptRefresh
        );
        await loadTags();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar tag' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadTags]
  );

  const handleDeleteTag = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.deleteTag(id),
          refreshToken,
          attemptRefresh
        );
        await loadTags();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir tag' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadTags]
  );

  // ============================================================
  // Font mutations
  // ============================================================

  const handleUploadFont = useCallback(
    async (file: File, family: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.uploadFont(file, family),
          refreshToken,
          attemptRefresh
        );
        await loadFonts();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao enviar fonte' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadFonts]
  );

  const handleDeleteFont = useCallback(
    async (id: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return notAuthenticated();
      setLoading(true);
      try {
        await withAuthRetry(
          () => blogService.deleteFont(id),
          refreshToken,
          attemptRefresh
        );
        await loadFonts();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir fonte' };
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, loadFonts]
  );

  // ============================================================
  // Editor navigation
  // ============================================================

  const setEditingPostIdCallback = useCallback((id: string | null) => {
    setEditingPostId(id);
    if (!id) {
      setCurrentPost(null);
    }
  }, []);

  return {
    // Data
    posts,
    currentPost,
    categories,
    tags,
    media,
    fonts,
    analytics,

    // UI state
    loading,
    blogTab,
    editingPostId,
    totalPosts,
    postsPage,

    // Actions
    loadPosts,
    loadPost,
    handleCreatePost,
    handleUpdatePost,
    handlePublishPost,
    handleDeletePost,
    handleAutoSave,
    loadCategories,
    handleCreateCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    loadTags,
    handleCreateTag,
    handleDeleteTag,
    loadMedia,
    loadFonts,
    handleUploadFont,
    handleDeleteFont,
    loadAnalytics,
    setBlogTab,
    setEditingPostId: setEditingPostIdCallback,
  };
}
